import { useRef, useState } from "react";
import { toast } from "sonner";
import {
  Upload, FileText, Loader2, X, AlertCircle, CheckCircle2, Eye, MapPin,
} from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { PROPERTY_TYPES } from "../../lib/propertyTypes";
import AuthImage from "../AuthImage";

/** Bulk CSV property import.
 *
 * Two-stage workflow:
 *   1) Sponsor pastes CSV text (or uploads a .csv file) → we POST to
 *      /api/properties/import/preview which geocodes every row in parallel.
 *   2) Sponsor reviews each row in a table (Street View thumbnail + status
 *      chip), checks/unchecks which to include, then commits to
 *      /api/properties/import/commit.
 *
 * No DB writes happen until step 2. Duplicate detection (against existing
 * properties in the owner's portfolio, matched on Google place_id) shows
 * inline in the table. */
export default function BulkImportDialog({ open, onClose, onImported }) {
  const [stage, setStage] = useState("input"); // input | preview | done
  const [csvText, setCsvText] = useState("");
  const [defaultType, setDefaultType] = useState("other");
  const [busy, setBusy] = useState(false);
  const [previewData, setPreviewData] = useState(null); // { rows, unrecognized_headers, plan }
  const [includeMap, setIncludeMap] = useState({});  // { [rowIndex]: bool }
  const [commitResult, setCommitResult] = useState(null);
  const fileInputRef = useRef(null);

  const reset = () => {
    setStage("input");
    setCsvText("");
    setDefaultType("other");
    setPreviewData(null);
    setIncludeMap({});
    setCommitResult(null);
  };

  const close = () => { reset(); onClose?.(); };

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      setCsvText(text);
    } catch {
      toast.error("Couldn't read file");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const runPreview = async () => {
    if (!csvText.trim()) { toast.error("Paste a CSV or upload a file first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/properties/import/preview", {
        csv_text: csvText,
        default_property_type: defaultType,
      });
      setPreviewData(data);
      // Default-include every row that geocoded successfully AND isn't a duplicate.
      const incl = {};
      for (const r of data.rows || []) {
        incl[r.index] = !!r.geocoded && !r.error && !r.duplicate_of;
      }
      setIncludeMap(incl);
      setStage("preview");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const toggleRow = (idx) => {
    setIncludeMap((m) => ({ ...m, [idx]: !m[idx] }));
  };
  const setAll = (val) => {
    const m = {};
    for (const r of previewData.rows) m[r.index] = val && !r.error;
    setIncludeMap(m);
  };

  const includedCount = Object.values(includeMap).filter(Boolean).length;

  const runCommit = async () => {
    const rows = previewData.rows
      .filter((r) => includeMap[r.index] && !r.error)
      .map((r) => ({
        name: r.name, address: r.address, property_type: r.property_type,
        units: r.units, purchase_price: r.purchase_price,
        market_value: r.market_value, annual_taxes: r.annual_taxes,
        annual_insurance: r.annual_insurance,
        notes: r.notes || null,
        lat: r.lat, lng: r.lng,
        formatted_address: r.formatted_address, place_id: r.place_id,
        has_streetview: r.has_streetview,
      }));
    if (rows.length === 0) { toast.error("Select at least one row"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/properties/import/commit", { rows });
      setCommitResult(data);
      setStage("done");
      onImported?.(data);
      if (data.created_count > 0) {
        toast.success(`${data.created_count} propert${data.created_count === 1 ? "y" : "ies"} imported`);
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="bulk-import-dialog">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={busy ? undefined : close} />
      <div className="relative w-full max-w-5xl bg-white rounded-sm shadow-2xl border border-slate-200 max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Properties · Bulk Import</div>
            <div className="text-base font-semibold text-slate-900">
              {stage === "input" && "Paste or upload CSV"}
              {stage === "preview" && `Review ${previewData?.rows?.length || 0} rows`}
              {stage === "done" && "Import complete"}
            </div>
          </div>
          <button onClick={close} disabled={busy} className="p-1.5 rounded-sm hover:bg-slate-100 text-slate-500 disabled:opacity-50"
            data-testid="bulk-import-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ============ STAGE: INPUT ============ */}
        {stage === "input" && (
          <div className="p-5 space-y-4">
            <div className="text-sm text-slate-600 leading-relaxed">
              Paste a CSV with one property per row, or upload a <code>.csv</code> file. We'll auto-geocode each address and let you review before importing. Up to <strong>500 rows</strong> per import.
            </div>

            <div className="rounded-sm border border-slate-200 bg-slate-50 px-3 py-2.5">
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">Recognized column headers</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-[11px] font-mono text-slate-600">
                <div><strong className="text-slate-800">address</strong> · required</div>
                <div><strong className="text-slate-800">name</strong> · optional (auto-derived)</div>
                <div><strong className="text-slate-800">property_type</strong> · optional</div>
                <div><strong className="text-slate-800">units</strong> · optional</div>
                <div><strong className="text-slate-800">purchase_price</strong> · optional</div>
                <div><strong className="text-slate-800">market_value</strong> · optional</div>
                <div><strong className="text-slate-800">annual_taxes</strong></div>
                <div><strong className="text-slate-800">annual_insurance</strong></div>
                <div><strong className="text-slate-800">purchase_date</strong> · YYYY-MM-DD</div>
                <div><strong className="text-slate-800">notes</strong></div>
              </div>
              <div className="text-[10px] text-slate-400 mt-1.5">
                We also accept these synonyms: <code>street_address</code>, <code>asset_type</code>, <code>doors</code>, <code>price</code>, <code>taxes</code>, etc.
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="md:col-span-3">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">CSV Text</div>
                  <button type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1 text-slate-700 hover:text-slate-900"
                    data-testid="bulk-import-upload-btn">
                    <Upload className="w-3 h-3" /> Upload .csv
                  </button>
                  <input type="file" accept=".csv,text/csv" ref={fileInputRef} className="hidden" onChange={onFileChange} />
                </div>
                <textarea
                  rows={10}
                  value={csvText}
                  onChange={(e) => setCsvText(e.target.value)}
                  placeholder={`address,property_type,units,purchase_price\n350 Fifth Avenue New York NY,office,102,2400000000\n100 W Main St Springfield IL,retail,1,850000`}
                  className="input-clean w-full text-xs font-mono leading-relaxed"
                  data-testid="bulk-import-csv-textarea"
                />
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">Default Type</div>
                <select
                  value={defaultType}
                  onChange={(e) => setDefaultType(e.target.value)}
                  className="input-clean w-full text-sm"
                  data-testid="bulk-import-default-type">
                  {PROPERTY_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                <div className="text-[9px] text-slate-400 mt-1.5 leading-snug">
                  Used when a CSV row leaves property_type blank or unrecognized.
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
              <button onClick={close}
                className="px-4 py-2 text-xs font-mono uppercase tracking-widest text-slate-500 hover:text-slate-800">
                Cancel
              </button>
              <button onClick={runPreview} disabled={busy || !csvText.trim()}
                data-testid="bulk-import-preview-btn"
                className="bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white px-5 py-2 rounded-sm text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1.5">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
                {busy ? "Geocoding…" : "Preview"}
              </button>
            </div>
          </div>
        )}

        {/* ============ STAGE: PREVIEW ============ */}
        {stage === "preview" && previewData && (
          <PreviewTable
            previewData={previewData}
            includeMap={includeMap}
            onToggleRow={toggleRow}
            onSetAll={setAll}
            includedCount={includedCount}
            busy={busy}
            onBack={() => setStage("input")}
            onCommit={runCommit}
          />
        )}

        {/* ============ STAGE: DONE ============ */}
        {stage === "done" && commitResult && (
          <div className="p-5 space-y-4">
            <div className="rounded-sm border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
              <div className="text-sm text-emerald-800">
                <strong>{commitResult.created_count}</strong> propert{commitResult.created_count === 1 ? "y" : "ies"} added to your portfolio.
              </div>
            </div>
            {commitResult.errors?.length > 0 && (
              <div className="rounded-sm border border-rose-200 bg-rose-50 px-4 py-3">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="w-4 h-4 text-rose-600" />
                  <div className="text-xs font-mono uppercase tracking-widest text-rose-700">
                    {commitResult.errors.length} row{commitResult.errors.length === 1 ? "" : "s"} failed
                  </div>
                </div>
                <ul className="text-xs text-rose-800 space-y-0.5">
                  {commitResult.errors.map((e, i) => (
                    <li key={i}>· {e.address || "(no address)"} — {e.error}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
              <button onClick={() => { reset(); }}
                className="px-4 py-2 text-xs font-mono uppercase tracking-widest text-slate-600 hover:text-slate-900"
                data-testid="bulk-import-another-btn">
                Import another
              </button>
              <button onClick={close} data-testid="bulk-import-done-btn"
                className="bg-slate-900 hover:bg-slate-800 text-white px-5 py-2 rounded-sm text-xs font-mono uppercase tracking-widest">
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PreviewTable({ previewData, includeMap, onToggleRow, onSetAll, includedCount, busy, onBack, onCommit }) {
  const okRows = previewData.rows.filter((r) => !r.error).length;
  const errRows = previewData.rows.length - okRows;
  const dupRows = previewData.rows.filter((r) => r.duplicate_of).length;
  const planExceeded = previewData.plan?.would_exceed_at && includedCount > (previewData.plan?.remaining_slots || 0);

  return (
    <div className="p-5 space-y-4">
      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        <SummaryChip label="Rows" value={previewData.rows.length} />
        <SummaryChip label="Geocoded" value={okRows} tone="emerald" />
        <SummaryChip label="Errors" value={errRows} tone={errRows > 0 ? "rose" : "slate"} />
        <SummaryChip label="Duplicates" value={dupRows} tone={dupRows > 0 ? "amber" : "slate"} />
      </div>

      {previewData.unrecognized_headers?.length > 0 && (
        <div className="rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
          <strong>Unrecognized headers ignored:</strong> {previewData.unrecognized_headers.join(", ")}
        </div>
      )}

      {planExceeded && (
        <div className="rounded-sm border border-rose-200 bg-rose-50 px-3 py-2 text-[11px] text-rose-800 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <strong>Plan limit exceeded.</strong> You have {previewData.plan.current_count}/{previewData.plan.max_properties} properties; only {previewData.plan.remaining_slots} more allowed. Uncheck rows or upgrade your plan.
          </div>
        </div>
      )}

      <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-slate-500">
        <div>
          <button onClick={() => onSetAll(true)} className="hover:text-slate-800" data-testid="bulk-import-select-all">Select all</button>
          <span className="text-slate-300 mx-1.5">/</span>
          <button onClick={() => onSetAll(false)} className="hover:text-slate-800" data-testid="bulk-import-clear-all">Clear</button>
        </div>
        <div data-testid="bulk-import-included-count">{includedCount} of {previewData.rows.length} included</div>
      </div>

      <div className="border border-slate-200 rounded-sm overflow-hidden" data-testid="bulk-import-preview-table">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-[10px] font-mono uppercase tracking-widest text-slate-500">
              <th className="px-2 py-2 w-8"></th>
              <th className="px-2 py-2">Preview</th>
              <th className="px-2 py-2">Address</th>
              <th className="px-2 py-2">Type</th>
              <th className="px-2 py-2 text-right">Price</th>
              <th className="px-2 py-2">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {previewData.rows.map((r) => {
              const checked = !!includeMap[r.index] && !r.error;
              const disabled = !!r.error;
              return (
                <tr key={r.index} className={r.error ? "bg-rose-50/30" : r.duplicate_of ? "bg-amber-50/30" : ""}
                  data-testid={`bulk-import-row-${r.index}`}>
                  <td className="px-2 py-2 align-middle">
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled}
                      onChange={() => onToggleRow(r.index)}
                      className="cursor-pointer disabled:cursor-not-allowed accent-slate-700"
                      data-testid={`bulk-import-row-check-${r.index}`}
                    />
                  </td>
                  <td className="px-2 py-2">
                    {r.geocoded ? (
                      <div className="w-20 h-14 bg-slate-100 rounded-sm overflow-hidden border border-slate-200">
                        <AuthImage
                          src={`/api/properties/image?lat=${r.lat}&lng=${r.lng}&size=240x160`}
                          alt={r.name}
                          className="w-full h-full object-cover"
                          placeholder={<div className="w-full h-full animate-pulse bg-slate-100" />}
                          errorFallback={<div className="w-full h-full flex items-center justify-center"><MapPin className="w-4 h-4 text-slate-400" /></div>}
                        />
                      </div>
                    ) : (
                      <div className="w-20 h-14 flex items-center justify-center bg-slate-100 rounded-sm border border-slate-200">
                        <FileText className="w-4 h-4 text-slate-400" />
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-2">
                    <div className="font-semibold text-slate-800 leading-tight">{r.name}</div>
                    <div className="text-[10px] text-slate-500 truncate max-w-xs" title={r.formatted_address || r.address}>
                      {r.formatted_address || r.address || "(no address)"}
                    </div>
                  </td>
                  <td className="px-2 py-2 font-mono uppercase text-[10px] text-slate-600">{r.property_type}</td>
                  <td className="px-2 py-2 font-mono text-right text-slate-700">
                    {r.purchase_price ? `$${Number(r.purchase_price).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "—"}
                  </td>
                  <td className="px-2 py-2">
                    {r.error ? (
                      <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border bg-rose-50 text-rose-700 border-rose-200">
                        {r.error}
                      </span>
                    ) : r.duplicate_of ? (
                      <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border bg-amber-50 text-amber-800 border-amber-200"
                        title={`Already in portfolio: ${r.duplicate_of.name}`}>
                        Duplicate
                      </span>
                    ) : (
                      <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border bg-emerald-50 text-emerald-700 border-emerald-200">
                        Ready
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
        <button onClick={onBack} disabled={busy}
          className="px-4 py-2 text-xs font-mono uppercase tracking-widest text-slate-500 hover:text-slate-800"
          data-testid="bulk-import-back-btn">
          Back
        </button>
        <button onClick={onCommit}
          disabled={busy || includedCount === 0 || planExceeded}
          data-testid="bulk-import-commit-btn"
          className="bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white px-5 py-2 rounded-sm text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1.5">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
          {busy ? "Importing…" : `Import ${includedCount} propert${includedCount === 1 ? "y" : "ies"}`}
        </button>
      </div>
    </div>
  );
}

function SummaryChip({ label, value, tone }) {
  const cls = {
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    rose: "bg-rose-50 text-rose-800 border-rose-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
  }[tone] || "bg-slate-50 text-slate-700 border-slate-200";
  return (
    <div className={`rounded-sm border px-3 py-2 ${cls}`}>
      <div className="text-[10px] font-mono uppercase tracking-widest opacity-80">{label}</div>
      <div className="text-base font-semibold font-mono mt-0.5">{value}</div>
    </div>
  );
}
